import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import IconLogo from '../assets/Icon_logo.svg';
import LandingHeader from '../components/LandingHeader';
import { useLanguage } from '../contexts/LanguageContext';

const PricingPage = () => {
  const { t } = useLanguage();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlan, setSelectedPlan] = useState('starter');

  const plans = [
    {
      id: 'starter',
      name: t('pricingPage.plans.starter.name'),
      description: t('pricingPage.plans.starter.description'),
      price: { monthly: 29, yearly: 290 },
      features: [
        t('pricingPage.plans.starter.features.0'),
        t('pricingPage.plans.starter.features.1'),
        t('pricingPage.plans.starter.features.2'),
        t('pricingPage.plans.starter.features.3'),
        t('pricingPage.plans.starter.features.4'),
        t('pricingPage.plans.starter.features.5')
      ],
      popular: false,
    },
    {
      id: 'professional',
      name: t('pricingPage.plans.professional.name'),
      description: t('pricingPage.plans.professional.description'),
      price: { monthly: 79, yearly: 790 },
      features: [
        t('pricingPage.plans.professional.features.0'),
        t('pricingPage.plans.professional.features.1'),
        t('pricingPage.plans.professional.features.2'),
        t('pricingPage.plans.professional.features.3'),
        t('pricingPage.plans.professional.features.4'),
        t('pricingPage.plans.professional.features.5'),
        t('pricingPage.plans.professional.features.6'),
        t('pricingPage.plans.professional.features.7')
      ],
      popular: true,
    },
    {
      id: 'enterprise',
      name: t('pricingPage.plans.enterprise.name'),
      description: t('pricingPage.plans.enterprise.description'),
      price: { monthly: 199, yearly: 1990 },
      features: [
        t('pricingPage.plans.enterprise.features.0'),
        t('pricingPage.plans.enterprise.features.1'),
        t('pricingPage.plans.enterprise.features.2'),
        t('pricingPage.plans.enterprise.features.3'),
        t('pricingPage.plans.enterprise.features.4'),
        t('pricingPage.plans.enterprise.features.5'),
        t('pricingPage.plans.enterprise.features.6'),
        t('pricingPage.plans.enterprise.features.7')
      ],
      popular: false,
    }
  ];

  const faqs = [
    { question: t('pricingPage.faq.questions.changePlan.question'), answer: t('pricingPage.faq.questions.changePlan.answer') },
    { question: t('pricingPage.faq.questions.freeTrial.question'), answer: t('pricingPage.faq.questions.freeTrial.answer') },
    { question: t('pricingPage.faq.questions.volumeDiscounts.question'), answer: t('pricingPage.faq.questions.volumeDiscounts.answer') },
    { question: t('pricingPage.faq.questions.setupFee.question'), answer: t('pricingPage.faq.questions.setupFee.answer') },
    { question: t('pricingPage.faq.questions.paymentMethods.question'), answer: t('pricingPage.faq.questions.paymentMethods.answer') },
  ];

  const comparisonFeatures = [
    t('pricingPage.comparison.properties'),
    t('pricingPage.comparison.tenantManagement'),
    t('pricingPage.comparison.maintenanceTracking'),
    t('pricingPage.comparison.serviceProviderNetwork'),
    t('pricingPage.comparison.rentCollection'),
    t('pricingPage.comparison.documentManagement'),
    t('pricingPage.comparison.analyticsReporting'),
    t('pricingPage.comparison.apiAccess'),
    t('pricingPage.comparison.prioritySupport'),
    t('pricingPage.comparison.whiteLabelSolutions'),
  ];

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200/60 bg-gradient-to-b from-slate-50 to-white py-20 sm:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(148,163,184,0.12),transparent)]" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <span className="mb-4 inline-block rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-600 shadow-sm">
            Pricing
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            {t('pricingPage.hero.title')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-600">
            {t('pricingPage.hero.subtitle')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-slate-800"
            >
              {t('pricingPage.hero.button')}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center rounded-xl border-2 border-slate-200 px-8 py-4 text-base font-semibold text-slate-800 transition hover:border-slate-300 hover:bg-slate-50"
            >
              {t('pricingPage.hero.viewFeatures')}
            </Link>
          </div>
        </div>
      </section>

      {/* Billing & Plans */}
      <section className="py-16 sm:py-24 border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t('pricingPage.pricing.title')}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-600">
              {t('pricingPage.pricing.description')}
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex justify-center mb-14">
            <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50/80 p-1.5">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t('pricingPage.pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`rounded-lg px-5 py-2.5 text-sm font-medium transition ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t('pricingPage.pricing.yearly')}
                <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                  {t('pricingPage.pricing.save20')}
                </span>
              </button>
            </div>
          </div>

          {/* Pricing cards */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border bg-white p-8 transition ${
                  plan.popular
                    ? 'border-slate-900 shadow-lg shadow-slate-900/10 ring-2 ring-slate-900/5'
                    : 'border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white">
                      {t('pricingPage.pricing.mostPopular')}
                    </span>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                  <p className="mt-2 text-sm text-slate-600">{plan.description}</p>
                </div>
                <div className="mb-6 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">
                    ${plan.price[billingCycle]}
                  </span>
                  <span className="text-slate-500">
                    /{billingCycle === 'monthly' ? t('pricingPage.pricing.perMonth') : t('pricingPage.pricing.perYear')}
                  </span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="mb-6 text-sm font-medium text-slate-600">
                    {t('pricingPage.pricing.saveAmount').replace('${amount}', (plan.price.monthly * 12) - plan.price.yearly)}
                  </p>
                )}
                <ul className="mb-8 space-y-3 flex-1">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 shrink-0 text-slate-600 mt-0.5" strokeWidth={2} />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`block w-full rounded-xl py-3.5 text-center text-sm font-semibold transition ${
                    plan.popular
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'border-2 border-slate-200 text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {plan.id === 'starter' ? t('pricingPage.pricing.startFreeTrial') : t('pricingPage.pricing.getStarted')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24 border-b border-slate-100 bg-slate-50/80">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t('pricingPage.faq.title')}
            </h2>
            <p className="mt-3 text-slate-600">{t('pricingPage.faq.subtitle')}</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 sm:py-24 border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {t('pricingPage.comparison.title')}
            </h2>
            <p className="mt-3 text-slate-600">{t('pricingPage.comparison.subtitle')}</p>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="py-4 px-5 text-left text-sm font-semibold text-slate-900">
                    {t('pricingPage.comparison.features')}
                  </th>
                  {plans.map((plan) => (
                    <th key={plan.id} className="py-4 px-5 text-center">
                      <div className="text-sm font-semibold text-slate-900">{plan.name}</div>
                      <div className="mt-1 text-xs text-slate-500">
                        ${plan.price[billingCycle]}/{billingCycle === 'monthly' ? t('pricingPage.pricing.perMonth').replace('/', '') : t('pricingPage.pricing.perYear').replace('/', '')}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comparisonFeatures.map((feature, index) => (
                  <tr key={index} className="bg-white hover:bg-slate-50/50">
                    <td className="py-4 px-5 text-sm font-medium text-slate-900">{feature}</td>
                    {plans.map((plan) => {
                      const hasFeature = plan.features.some(f =>
                        f.toLowerCase().includes(feature.toLowerCase())
                      ) ||
                        (feature === t('pricingPage.comparison.apiAccess') && plan.id === 'enterprise') ||
                        (feature === t('pricingPage.comparison.whiteLabelSolutions') && plan.id === 'enterprise');
                      return (
                        <td key={plan.id} className="py-4 px-5 text-center">
                          {hasFeature ? (
                            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                              <Check className="h-4 w-4" strokeWidth={2} />
                            </div>
                          ) : (
                            <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                              <X className="h-4 w-4" strokeWidth={2} />
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-amber-200 bg-amber-400 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-amber-950 sm:text-4xl">
            {t('pricingPage.cta.title')}
          </h2>
          <p className="mt-5 text-lg text-amber-900/90">
            {t('pricingPage.cta.subtitle')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-950 px-8 py-4 text-base font-semibold text-amber-50 shadow-lg transition hover:bg-amber-900 sm:w-auto"
            >
              {t('pricingPage.cta.earlyAccess')}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </Link>
            <Link
              to="/features"
              className="inline-flex w-full items-center justify-center rounded-xl border-2 border-amber-800 px-8 py-4 text-base font-semibold text-amber-950 transition hover:bg-amber-500/30 sm:w-auto"
            >
              {t('pricingPage.cta.viewFeatures')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-14 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <img className="h-10 w-auto opacity-90" src={IconLogo} alt="EasyProperty" />
              <p className="mt-4 text-sm text-slate-400">{t('footer.description')}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.platform')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><Link to="/register" className="transition hover:text-white">{t('footer.signUp')}</Link></li>
                <li><Link to="/login" className="transition hover:text-white">{t('footer.signIn')}</Link></li>
                <li><Link to="/pricing" className="transition hover:text-white">{t('footer.pricing')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.support')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.helpCenter')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.contactUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.privacyPolicy')}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">{t('footer.connect')}</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.aboutUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.blog')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.careers')}</button></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>&copy; 2024 EasyProperty. {t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
